import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { BellIcon } from '@navikt/aksel-icons';
import type { WorkEnvironmentSurveyProgress } from '../../../api/workEnvironmentTypes';
import type { ReminderStatus } from '../reminderView';
import { dayAndMonth, daysLeft, responsePercent } from '../surveyProcessView';
import classes from '../WorkEnvironmentSurveyProcess.module.css';

type Props = {
  progress: WorkEnvironmentSurveyProgress;
  remind: ReminderStatus & { run: () => void; pending: boolean };
};

export const AnsweredCard = ({ progress, remind }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { responses, expected, responseDeadline, lastRemindedAt, canRemind } = progress;
  const percent = responsePercent(responses, expected);
  const left = responseDeadline ? daysLeft(responseDeadline, new Date()) : null;

  const note = (() => {
    if (responses >= expected) return t('workEnvironmentProcess.answered.everyone');
    if (remind.sent) return remind.sent;
    if (lastRemindedAt)
      return t('workEnvironmentProcess.answered.lastReminded', { date: dayAndMonth(lastRemindedAt, i18n.language) });
    return t('workEnvironmentProcess.answered.remindNote');
  })();

  return (
    <section aria-labelledby="answered-heading" className={`${classes.card} ${classes.main}`}>
      <div className={classes.cardBody}>
        <div className={classes.cardHeading}>
          <Heading level={2} data-size="sm" id="answered-heading">
            {t('workEnvironmentProcess.answered.title')}
          </Heading>
          {left !== null && (
            <Paragraph data-size="sm" className={classes.subtle}>
              {left > 0
                ? t('workEnvironmentProcess.answered.daysLeft', { count: left })
                : t('workEnvironmentProcess.answered.deadlinePassed')}
            </Paragraph>
          )}
        </div>
        <div className={classes.counts}>
          <div className={classes.count}>
            <span className={`ds-heading ${classes.countNumber}`} data-size="xl">
              {responses}
            </span>
            <span className={`ds-paragraph ${classes.subtle}`}>
              {t('workEnvironmentProcess.answered.count', { expected, percent })}
            </span>
          </div>
          <div
            role="progressbar"
            aria-label={t('workEnvironmentProcess.answered.progress')}
            aria-valuemin={0}
            aria-valuemax={expected}
            aria-valuenow={responses}
            className={classes.progressBar}
          >
            <span style={{ width: `${Math.min(percent, 100)}%` }} />
          </div>
        </div>
        <Alert data-color="info" data-size="sm">
          <Paragraph data-size="sm">{t('workEnvironmentProcess.answered.anonymous')}</Paragraph>
        </Alert>
      </div>
      <div className={classes.cardFooter}>
        {canRemind && !remind.sent && (
          <Button variant="primary" onClick={remind.run} loading={remind.pending}>
            <BellIcon aria-hidden />
            {t('workEnvironmentProcess.answered.remind')}
          </Button>
        )}
        <Paragraph data-size="sm" role="status" className={classes.subtle}>
          {note}
        </Paragraph>
        {remind.failed && (
          <Alert data-color="danger" data-size="sm">
            <Paragraph data-size="sm">{remind.failed}</Paragraph>
          </Alert>
        )}
      </div>
    </section>
  );
};
